import { usePremiumCurrencyFormatter } from '@wix/premium-react-text';
import { Experiments, PaymentCycle } from 'enums';
import { RootStore, withInjectedRootStore } from 'providers';
import * as React from 'react';
import { CycleDetails } from 'types';
import { SaleBadge } from './SaleBadge';
import { Tooltip } from '@wix/design-system';
import { useTranslation } from 'react-i18next';
import { DiscountBadge } from './DiscountBadge';
import { SaleDiscountBadge } from './SaleDiscountBadge/SaleDiscountBadge';
import { RevShareBadge } from './RevShareBadge/RevShareBadge';
import { ResellerDiscountBadge } from './ResellerDiscountBadge/ResellerDiscountBadge';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { getCycleKey, getSaleDiscountPercentage, isCycleOfMonths } from 'utils';

export interface BadgeCompProps extends InjectedExperimentsProps {
  cycle: CycleDetails;
  rootStore: RootStore;
  isCycleSelected: boolean;
}

const BadgeComp: React.FunctionComponent<BadgeCompProps> = ({
  cycle,
  isCycleSelected,
  rootStore,
}) => {
  const { t } = useTranslation();
  const [currencyToString] = usePremiumCurrencyFormatter();

  const {
    cycleId,
    fullTermDisplaySavings,
    monthlyFullPrice,
    fullTermActualPrice,
    firstPeriodPrice,
    isCycleDisabledInProduct,
    fullTermDisplayPrice,
  } = cycle;
  const {
    isInSale,
    isSaleProduct,
    appContext,
    isSecondUpgrade,
    hasMonthlyCycle,
    isChangePlan,
    experiments,
    isEligibleForRevShare,
    currentProduct,
    revShareData,
    isResellerOffering,
  } = rootStore.appStore;
  const { currentPlanDetails, advancedPlansProducts } = appContext;

  const _discountPercent = isResellerOffering
    ? advancedPlansProducts.find(
        (advancedProduct) => advancedProduct.id === currentProduct.id,
      )?.saleDiscountPercentage || 0
    : getSaleDiscountPercentage();
  const unifiedChangePlanPhase1Enabled =
    experiments[Experiments.UnifiedChangePlanPhase1] === 'true';
  const isUnifiedChangePlan = unifiedChangePlanPhase1Enabled && isChangePlan;

  const isCurrentCycleOrLowerSecondUpgrade = () =>
    (isSecondUpgrade || isUnifiedChangePlan) &&
    (isCycleDisabledInProduct || currentPlanDetails?.paymentCycle === cycleId);

  const saveAmount = fullTermDisplayPrice - fullTermActualPrice;

  const shouldShowDiscountBadge =
    hasMonthlyCycle &&
    !isInSale &&
    !isCycleOfMonths(cycleId) &&
    (fullTermDisplaySavings || firstPeriodPrice) &&
    !isCurrentCycleOrLowerSecondUpgrade();

  const shouldShowSaleBadge = () => {
    const hasDiscount = !!saveAmount;

    const isEligibleProduct =
      isResellerOffering || isSaleProduct || !!firstPeriodPrice;

    const isCycleValid = isResellerOffering || !isCycleOfMonths(cycleId);

    const isRestrictedUpgrade =
      (isSecondUpgrade || isUnifiedChangePlan) && isCycleDisabledInProduct;

    return (
      hasDiscount && isEligibleProduct && isCycleValid && !isRestrictedUpgrade
    );
  };

  const cycleDiscountTooltipIsOpenedBi = () => {
    const {
      appContext: { biLogger, siteGuid },
      planType,
    } = rootStore.appStore;

    biLogger.logDiscountTooltipIsShown({
      ppType: planType,
      siteGuid,
    });
  };

  const discountBadgeTooltipContent = () =>
    t('cycle_entry.second_upgrade.save_badge_tooltip', {
      amount: currencyToString(Math.floor(fullTermDisplaySavings), {
        hideZeroDecimals: true,
      }),
      recommendedCycle: t(getCycleKey(cycleId)),
      currentCycle: t(getCycleKey(PaymentCycle.MONTHLY)),
    });

  const renderDiscountBadge = () => (
    <DiscountBadge
      logBi={cycleDiscountTooltipIsOpenedBi}
      cycleId={cycleId}
      discount={fullTermDisplaySavings}
      isCycleSelected={isCycleSelected}
    />
  );
  if (isResellerOffering && !isCycleDisabledInProduct && !!saveAmount) {
    return (
      <>
        <SaleBadge isCycleSelected={isCycleSelected} cycle={cycle} />
        <ResellerDiscountBadge
          isCycleSelected={isCycleSelected}
          discount={_discountPercent}
          cycleId={cycleId}
        />
      </>
    );
  }

  if (isEligibleForRevShare) {
    return (
      <RevShareBadge
        isCycleSelected={isCycleSelected}
        cycle={cycle}
        revShareData={revShareData}
        productId={currentProduct?.guid}
      />
    );
  }

  if (shouldShowDiscountBadge) {
    return isSecondUpgrade || isUnifiedChangePlan ? (
      <Tooltip
        content={discountBadgeTooltipContent()}
        maxWidth={250}
        dataHook='discount-badge-tooltip'
      >
        {renderDiscountBadge()}
      </Tooltip>
    ) : (
      renderDiscountBadge()
    );
  }

  if (shouldShowSaleBadge()) {
    return (
      <>
        <SaleBadge isCycleSelected={isCycleSelected} cycle={cycle} />
        <SaleDiscountBadge
          logBi={cycleDiscountTooltipIsOpenedBi}
          discount={_discountPercent}
          cycleId={cycleId}
          monthlyFullPrice={monthlyFullPrice}
          isCycleSelected={isCycleSelected}
          fullTermActualPrice={fullTermActualPrice}
        />
      </>
    );
  }
  return null;
};

export const Badge = withInjectedRootStore(withExperiments(BadgeComp));
